<template>
    <div id="survey-list">
        <v-card>
            <v-card-title> {{ $t("Kyselyt") }} </v-card-title>

            <v-card-text>
                <v-alert color="info" text>
                    {{ $t("surveyinfo2") }}
                </v-alert>
            </v-card-text>

            <v-card-text class="d-flex space-between flex-wrap pb-0">
                <v-btn icon @click="toggleSearch">
                    <v-icon>
                        {{ icons.mdiMagnify }}
                    </v-icon>
                </v-btn>
                <v-spacer></v-spacer>
                <div class="d-flex align-center flex-wrap">
                    <v-btn color="primary" class="mb-4" :to="{ name: 'admin-survey-form' }">
                        <span>{{ $t("Uusi kysely") }}</span>
                    </v-btn>
                </div>
            </v-card-text>

            <v-card-text v-if="showSearch">
                <v-text-field v-model="searchQuery" :label="$t('Hae otsikolla')" dense outlined
                    clearable></v-text-field>
            </v-card-text>

            <v-data-table :headers="tableColumns" :items="surveys" :server-items-length="totalCount"
                :items-per-page="10" :options.sync="options" :loading="loading" mobile-breakpoint="0" :footer-props="{
                    'items-per-page-options': [10],
                }">
                <!-- title -->
                <template #[`item.title`]="{ item }">
                    <div class="d-flex align-center">
                        <router-link :to="{ name: 'admin-survey-form', params: { id: item.id } }"
                            class="font-weight-medium text-decoration-none">
                            {{ item.title }}
                        </router-link>
                    </div>
                </template>

                <!-- groups -->
                <template #[`item.groups`]="{ item }">
                    <template v-if="item.groups.length <= 2">
                        <v-chip v-for="group in item.groups" :key="group.id" label small color="primary" class="mr-1">
                            {{ group.name }}
                        </v-chip>
                    </template>
                    <template v-else>
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-chip label x-small color="secondary" v-bind="attrs" v-on="on">
                                    {{ item.groups.length }} ryhmää
                                </v-chip>
                            </template>
                            <span>{{item.groups.map(g => g.name).join(', ')}}</span>
                        </v-tooltip>
                    </template>
                </template>

                <!-- reply_time -->
                <template #[`item.reply_time`]="{ item }">
                    <v-icon v-if="isSurveyActive(item)" size="18" color="success">{{ icons.mdiCheckCircle }}</v-icon>
                    <v-icon v-else size="18" color="error">{{ icons.mdiCloseCircle }}</v-icon>
                    <span v-if="item.release_date && item.expire_date">
                        {{ getDateTime(item.release_date) }} - {{ getDateTime(item.expire_date) }}
                    </span>
                    <span v-else class="grey--text">-</span>
                </template>

                <!-- submissions -->
                <template #[`item.submissions`]="{ item }">
                    <router-link v-if="item.submission_count > 0" :to="{
                        name: 'admin-survey-submissions',
                        params: { id: item.id },
                    }" class="text--primary font-weight-semibold text-truncate cursor-pointer text-decoration-none">
                        {{ item.submission_count }} {{ $t("vastausta") }} &raquo;
                    </router-link>
                    <span v-else class="grey--text">{{ $t("Ei vastauksia") }}</span>
                </template>

                <!-- actions -->
                <template #[`item.actions`]="{ item }">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon small v-bind="attrs" v-on="on" @click="copySurvey(item.id)"
                                :loading="copying === item.id">
                                <v-icon small>{{ icons.mdiContentCopy }}</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t('Kopioi kysely') }}</span>
                    </v-tooltip>
                </template>
            </v-data-table>
        </v-card>
    </div>
</template>

<script>
import { onMounted, ref, watch, computed } from "@vue/composition-api";
import { useRouter } from "@/@core/utils";
import adminApi from "@/api/admin";
import { mdiMagnify, mdiCheckCircle, mdiCloseCircle, mdiContentCopy } from "@mdi/js";
import { useTranslations } from "@core/libs/i18n";
import { getDateTime } from "@/@core/utils/datetime_helpers";
import moment from "moment";

export default {
    setup() {
        const { router } = useRouter();
        const surveys = ref([]);
        const totalCount = ref(0);
        const searchQuery = ref("");
        const showSearch = ref(false);
        const copying = ref(null);

        const page = ref(1);
        const options = ref({});
        const loading = ref(false);

        const { t } = useTranslations();

        const tableColumns = computed(() => [
            { text: t("Otsikko").toUpperCase(), value: "title", sortable: false },
            { text: t("Ryhmät").toUpperCase(), value: "groups", sortable: false },
            { text: t("Vastausaika").toUpperCase(), value: "reply_time", sortable: false },
            { text: t("Vastaukset").toUpperCase(), value: "submissions", sortable: false },
            { text: t("").toUpperCase(), value: "actions", sortable: false, align: "end" },
        ]);

        const isSurveyActive = (survey) => {
            if (!survey.release_date || !survey.expire_date) {
                return false;
            }
            return moment(new Date()).isBetween(survey.release_date, survey.expire_date);
        };

        // fetch data
        const fetchSurveys = async () => {
            loading.value = true;
            const response = await adminApi.getSurveysList(page.value, 10, searchQuery.value);

            if (response.ok) {
                surveys.value = response.data.results;
                totalCount.value = response.data.count;
            }

            loading.value = false;
        };

        watch(options, () => {
            page.value = options.value.page;
            fetchSurveys();
        });

        let searchTimeout = null;
        watch(searchQuery, () => {
            clearTimeout(searchTimeout);
            searchTimeout = setTimeout(() => {
                if (searchQuery.value) {
                    if (searchQuery.value.length > 3) {
                        options.value = { ...options.value, page: 1 };
                        page.value = options.value.page;
                    }
                } else {
                    options.value = { ...options.value, page: 1 };
                    page.value = options.value.page;
                }
            }, 600);
        });

        const copySurvey = async (surveyId) => {
            copying.value = surveyId;
            const response = await adminApi.copySurvey(surveyId);

            if (response.ok) {
                options.value = { ...options.value, page: 1 };
                page.value = options.value.page;
            } else {
                alert(response.error || t('Kyselyn kopiointi epäonnistui'));
            }
            copying.value = null;
        };

        const toggleSearch = () => {
            if (showSearch.value) searchQuery.value = "";
            showSearch.value = !showSearch.value;
        };

        return {
            surveys,
            searchQuery,
            totalCount,
            copying,
            copySurvey,
            toggleSearch,
            showSearch,
            tableColumns,
            options,
            loading,
            getDateTime,
            isSurveyActive,
            icons: {
                mdiMagnify,
                mdiCheckCircle,
                mdiCloseCircle,
                mdiContentCopy,
            },
            t,
        };
    },
};
</script>

<style></style>
